import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthUserProfile } from '../types';
import { authService } from '../services/authService';
import { clearAuthSession, isAuthenticated } from '../services/authStorage';

interface AdminPortalContextValue {
  initialized: boolean;
  loading: boolean;
  isAdmin: boolean;
  profile: AuthUserProfile | null;
  error: string | null;
  refreshSession: () => Promise<void>;
  signOut: () => void;
}

const AdminPortalContext = createContext<AdminPortalContextValue | undefined>(undefined);

export const AdminPortalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [profile, setProfile] = useState<AuthUserProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  const signOut = useCallback(() => {
    clearAuthSession();
    setIsAdmin(false);
    setProfile(null);
    setError(null);
  }, []);

  const refreshSession = useCallback(async () => {
    if (!isAuthenticated()) {
      setInitialized(true);
      setLoading(false);
      setIsAdmin(false);
      setProfile(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const nextProfile = await authService.getProfile();
      const nextIsAdmin = nextProfile.role === 'ADMIN';

      if (!nextIsAdmin) {
        signOut();
        setError('Acesso restrito ao admin da plataforma');
        return;
      }

      setProfile(nextProfile);
      setIsAdmin(true);
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar sessao do portal admin');
      if (nextError.status === 401 || nextError.status === 403) {
        signOut();
      }
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [signOut]);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const value = useMemo<AdminPortalContextValue>(
    () => ({
      initialized,
      loading,
      isAdmin,
      profile,
      error,
      refreshSession,
      signOut,
    }),
    [initialized, loading, isAdmin, profile, error, refreshSession, signOut]
  );

  return <AdminPortalContext.Provider value={value}>{children}</AdminPortalContext.Provider>;
};

export const useAdminPortal = (): AdminPortalContextValue => {
  const context = useContext(AdminPortalContext);
  if (!context) {
    throw new Error('useAdminPortal deve ser usado dentro de AdminPortalProvider');
  }
  return context;
};